import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AssessmentPage from 'on_web/components/shared/AssessmentPage/AssessmentPage';
import { Grid, Typography, Box, Divider } from '@material-ui/core';
import useCheckScreenSize from 'on_web/hooks/useCheckScreenSize';
import { makeStyles } from '@material-ui/core/styles';
import {
  COOL_BLUE,
  APP_SECTION_GENERAL,
  APP_SECTION_LIFESTYLE,
  APP_SECTION_DISEASE_HISTORY,
  APP_SECTION_MEDICATIONS,
  APP_SECTION_ENVIRONMENT,
} from 'colours';
import HealthProfileSection from 'on_web/components/shared/HealthProfile/HealthProfileSection';
import { requiresProfileRevisit } from 'on_web/helpers/redirectHelper';
import CustomInfoPopup from 'on_web/components/shared/Popup/CustomInfoPopup';
import ErrorMessageTile from 'on_web/components/shared/ErrorMessageTile/ErrorMessageTile';
import CustomDialog from 'on_web/components/shared/CustomDialog/CustomDialog';

import { FemaleIcon, MaleIcon } from 'on_web/assets/iconsList';
import {
  sleep1,
  sleep2,
  sleep3,
  sleep4,
  sleep5,
  stress1,
  stress2,
  stress3,
  stress4,
  stress5,
  exercise1,
  exercise2,
  exercise3,
  exercise4,
  exercise5,
  drinkAlcohol1,
  drinkAlcohol2,
  drinkAlcohol3,
  drinkAlcohol4,
  drinkAlcohol5,
  smoke1,
  smoke2,
  smoke3,
  smoke4,
  smoke5,
} from 'on_web/assets/imageList';
import useUsersApi from 'on_web/hooks/useUsersApi';
import {
  getQuestionsEthnicity,
  getAceValuesArr,
  getAceQuestions,
  getArbValuesArr,
  getArbQuestions,
  getCovid19TestedQuestions,
  getCovid19AntibodiesTestedQuestions,
  getCovid19Contact,
  getComorbiditiesValuesArr,
  getComorbiditiesQuestions,
  getSymptomsQuestions,
  getHealthQuestions,
  getLivingArrangementsQuestions,
  getWorkStatusQuestions,
  getWorktimeQuestions,
  getSelectedWorkPeopleQuestions,
  getWeightMetrics,
  getHeightMetrics,
  getMedicationsQuestionsSwitch,
} from 'on_web/helpers/questions';
import {
  getUserWeight,
  getUserHeight,
  getUserHeight2,
} from 'on_web/helpers/converterHelper';

export default function HealthProfilePage(props) {
  const { t } = useTranslation();
  const [isDesktop] = useCheckScreenSize();
  const [showPopup, setShowPopup] = useState(false);
  const [showError, setShowError] = useState(false);

  const useStyles = makeStyles(theme => ({
    list: {
      paddingBottom: '10px',
      wordWrap: 'break-word',
    },
    link: {
      color: COOL_BLUE,
    },
  }));
  const classes = useStyles();

  const [
    {
      response,
      loading,
      responseProfile,
      getUserData,
      getProfile,
      responseACE,
      getACE,
      responseARB,
      getARB,
      responseCovidPastInfections,
      getCovidPastInfections,
      responseCovidComorbidities,
      getCovidComorbidities,
      responseCovidSymptoms,
      getCovidSymptoms,
      getDiseases,
      responseDiseases,
      getCovidLifestyle,
      responseCovidLifestyle,
      partialUpdateUserData,
    },
  ] = useUsersApi();

  useEffect(() => {
    getUserData().then(resp => {
      if (
        !resp.user_metadata ||
        !resp.user_metadata.onboarding ||
        !resp.user_metadata.onboarding.onboardingFinished
      ) {
        setShowError(true);
      }
    });
  }, [getUserData]);

  useEffect(() => {
    if (response != null) {
      getProfile().then(r => {
        if (requiresProfileRevisit(r)) {
          if (
            response.user_metadata &&
            response.user_metadata.updateSpareProfileDisplayed !== true
          ) {
            partialUpdateUserData({
              user_metadata: { updateSpareProfileDisplayed: true },
            }).then(() => setShowPopup(true));
          }
        }
      });
      getACE();
      getARB();
      getCovidPastInfections();
      getCovidComorbidities();
      getCovidSymptoms();
      getDiseases();
      getCovidLifestyle();
    }
  }, [response]);

  const getHeight = (height, metric) => {
    if (metric === 0) {
      return `${getUserHeight(height, metric)}'${getUserHeight2(
        height,
        metric
      )}''`;
    } else {
      return getUserHeight(height, metric);
    }
  };

  const sleepImages = [
    {
      image: sleep1,
      height: 75,
    },
    {
      image: sleep2,
      height: 75,
    },
    {
      image: sleep3,
      height: 75,
    },
    {
      image: sleep4,
      height: 75,
    },
    {
      image: sleep5,
      height: 75,
    },
  ];

  const sleepAnswers = [
    t('onboarding.lifestyleSection.lifestyleSleepStress.sleepAnswer1'),
    t('onboarding.lifestyleSection.lifestyleSleepStress.sleepAnswer2'),
    t('onboarding.lifestyleSection.lifestyleSleepStress.sleepAnswer3'),
    t('onboarding.lifestyleSection.lifestyleSleepStress.sleepAnswer4'),
    t('onboarding.lifestyleSection.lifestyleSleepStress.sleepAnswer5'),
  ];

  const stressImages = [
    {
      image: stress1,
      height: 75,
    },
    {
      image: stress2,
      height: 75,
    },
    {
      image: stress3,
      height: 75,
    },
    {
      image: stress4,
      height: 75,
    },
    {
      image: stress5,
      height: 75,
    },
  ];

  const stressAnswers = [
    t('common.answers.answerVeryLow'),
    t('common.answers.answerLow'),
    t('common.answers.answerMedium'),
    t('common.answers.answerHigh'),
    t('common.answers.answerVeryHigh'),
  ];

  const exerciseImages = [
    {
      image: exercise1,
      width: 96,
    },
    {
      image: exercise2,
      width: 96,
    },
    {
      image: exercise3,
      width: 96,
    },
    {
      image: exercise4,
      width: 96,
    },
    {
      image: exercise5,
      width: 96,
    },
  ];

  const exerciseAnswers = [
    t('onboarding.lifestyleSection.lifestyleExercise.answer1'),
    t('onboarding.lifestyleSection.lifestyleExercise.answer2'),
    t('onboarding.lifestyleSection.lifestyleExercise.answer3'),
    t('onboarding.lifestyleSection.lifestyleExercise.answer4'),
    t('onboarding.lifestyleSection.lifestyleExercise.answer5'),
  ];

  const smokingImages = [
    {
      image: smoke1,
      width: 115,
    },
    {
      image: smoke2,
      width: 115,
    },
    {
      image: smoke3,
      width: 115,
    },
    {
      image: smoke4,
      width: 115,
    },
    {
      image: smoke5,
      width: 115,
    },
  ];

  const smokingAnswers = [
    t('common.answers.answerNone'),
    t('common.answers.answer1to5'),
    t('common.answers.answer5to10'),
    t('common.answers.answer10to20'),
    t('common.answers.answer20+'),
  ];

  const drinkingImages = [
    {
      image: drinkAlcohol1,
      width: 145,
    },
    {
      image: drinkAlcohol2,
      width: 139,
    },
    {
      image: drinkAlcohol3,
      width: 131,
    },
    {
      image: drinkAlcohol4,
      width: 123,
    },
    {
      image: drinkAlcohol5,
      width: 115,
    },
  ];

  const drinkingAnswers = [
    t('common.answers.answerNone'),
    t('common.answers.answer1to2'),
    t('common.answers.answer3to5'),
    t('common.answers.answer6to8'),
    t('common.answers.answer9plus'),
  ];

  const getMedicationCards = userMetadata => {
    let arr = [];
    if (userMetadata.skipMedications !== true) {
      let medicationsQuestionsSwitch = getMedicationsQuestionsSwitch(
        null,
        t
      ).find(q => q.value === userMetadata.bloodPressureMedication);
      arr.push({
        title: 'Medication for high blood pressure / hypertension use',
        value: medicationsQuestionsSwitch
          ? medicationsQuestionsSwitch.label
          : '',
        valueColor: APP_SECTION_DISEASE_HISTORY,
        span: 12,
        redirectPath: 'onboarding-diseases',
        redirectStep: 5,
      });

      if (userMetadata.bloodPressureMedication === '1') {
        let selectedItemsACE = [];
        if (userMetadata.medicationsAceNone === true) {
          selectedItemsACE = ['11'];
        } else {
          selectedItemsACE = getAceValuesArr(responseACE);
        }

        const qsACE = getAceQuestions(selectedItemsACE, t);
        const answersACE = qsACE
          .filter(q => q.checked)
          .map(q => q.label)
          .join(', ');

        arr.push({
          title: 'ACE inhibitor use',
          value: answersACE,
          valueColor: APP_SECTION_DISEASE_HISTORY,
          span: 12,
          redirectPath: 'onboarding-diseases',
          redirectStep: 5,
        });

        let selectedItemsARB = [];
        if (userMetadata.medicationsArbNone === true) {
          selectedItemsARB = ['11'];
        } else {
          selectedItemsARB = getArbValuesArr(responseARB);
        }

        const qsARB = getArbQuestions(selectedItemsARB, t);
        const answersARB = qsARB
          .filter(q => q.checked)
          .map(q => q.label)
          .join(', ');

        arr.push({
          title: 'ARB medications use',
          value: answersARB,
          valueColor: APP_SECTION_DISEASE_HISTORY,
          span: 12,
          redirectPath: 'onboarding-diseases',
          redirectStep: 5,
        });
      }
    }

    return arr;
  };

  const getPreviouslyDiagnosedWithCard = () => {
    let selectedItems = [];
    if (response.user_metadata.covidComorbiditiesAreNone === true) {
      selectedItems = ['17'];
    } else {
      selectedItems = getComorbiditiesValuesArr(responseCovidComorbidities);
    }

    const qsComorbidities = getComorbiditiesQuestions(selectedItems, t);
    const answersComorbidities = qsComorbidities
      .filter(q => q.checked)
      .map(q => q.label)
      .join(', ');

    return {
      title: 'Previously diagnosed with',
      value: answersComorbidities,
      valueColor: APP_SECTION_DISEASE_HISTORY,
      span: 12,
      redirectPath: 'onboarding-diseases',
      redirectStep: 4,
    };
  };

  const getHealthCard = () => {
    let selectedItems = response.user_metadata.diseasesData;

    const qs = getHealthQuestions(selectedItems, responseDiseases, t);
    let answers = qs
      .filter(q => q.checked && q.value !== '8')
      .map(q => q.label)
      .join(', ');
    if (answers === '') {
      answers = '-';
    }

    return {
      title: 'Current health status',
      value: answers,
      valueColor: APP_SECTION_DISEASE_HISTORY,
      span: 12,
      redirectPath: 'onboarding-diseases',
      redirectStep: 6,
    };
  };

  const getEnvironmentCards = () => {
    let selectedItems =
      response.user_metadata.livingArrangements &&
      response.user_metadata.livingArrangements.selectedItems
        ? response.user_metadata.livingArrangements.selectedItems
        : [];

    const qs = getLivingArrangementsQuestions(selectedItems, t);
    const answers = qs
      .filter(q => q.checked)
      .map(q => q.label)
      .join(', ');

    let arr = [];
    arr.push({
      title: 'Living arrangements',
      value: answers,
      valueColor: APP_SECTION_DISEASE_HISTORY,
      span: 12,
      redirectPath: 'onboarding-environment',
      redirectStep: 1,
    });

    const workStatus = response.user_metadata.employmentWorkStatus;
    const workStatusAnswer = getWorkStatusQuestions(null, t).find(
      q => q.value === workStatus
    );
    let value =
      workStatus != null && workStatusAnswer != null
        ? workStatusAnswer.label
        : '-';

    if (workStatus === '1') {
      const workTime = response.user_metadata.employmentWorkTime;
      if (workTime != null) {
        const answer = getWorktimeQuestions(null, t).find(
          q => q.value === workTime
        );
        if (answer != null) {
          value = value + ', ' + answer.label;
        }
      }

      const workEnv = response.user_metadata.employmentWorkEnvironment;
      if (workEnv != null) {
        const answer = getSelectedWorkPeopleQuestions(null, t).find(
          q => q.value === workEnv
        );
        if (answer != null) {
          value = value + ', ' + answer.label;
        }
      }
    }

    arr.push({
      title: 'Work environment',
      value: value,
      valueColor: APP_SECTION_DISEASE_HISTORY,
      span: 12,
      redirectPath: 'onboarding-environment',
      redirectStep: 2,
    });

    return arr;
  };

  const getCovid19Tested = () => {
    return getCovid19TestedQuestions(null, t).find(q =>
      responseCovidPastInfections.tested_for_covid_19 === 1
        ? q.value === '1'
        : responseCovidPastInfections.tested_for_covid_19 === 0
        ? q.value === '2'
        : q.value === -1
    );
  };

  const getCovid19AntibodiesTested = () => {
    return getCovid19AntibodiesTestedQuestions(null, t).find(q =>
      responseCovidPastInfections.tested_for_covid_19_antibodies === 1
        ? q.value === '1'
        : responseCovidPastInfections.tested_for_covid_19_antibodies === 0
        ? q.value === '2'
        : q.value === -1
    );
  };

  const getContact = () => {
    return getCovid19Contact(null, t).find(
      q =>
        q.value ===
        '' +
          responseCovidPastInfections.contact_with_individual_covid_19_diagnosis_symptoms
    );
  };

  return !showError ? (
    <AssessmentPage
      headerMenuText={t('common.menu.mainItems.profile')}
      bottomMenuSelectedOptionCode={'profile'}
      desktopMenuIndex={5}
      secondaryHeaderWithBackNavigation={true}
      covid19Risks={
        props.location &&
        props.location.state &&
        props.location.state.covid19Risks
          ? props.location.state.covid19Risks
          : null
      }
    >
      <CustomInfoPopup
        open={showError}
        text={
          'It been a while since you’ve last updated your profile. Could you please take a look to make sure it’s up to date?'
        }
        onClick={() => setShowPopup(false)}
        buttonLabel={'OK'}
      />
      <Box px={'20px'} py={'10px'} mb={'60px'}>
        <Grid container justify="center" direction="column">
          {isDesktop && (
            <Grid
              item
              xs={12}
              style={{ paddingTop: '60px', paddingBottom: '40px' }}
            >
              <Typography variant={'h2'}>
                {t('common.menu.mainItems.profile')}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Box display={'block'} textAlign={'center'}>
              <Typography
                variant={'body1'}
                style={{ paddingBottom: '15px', fontSize: '14px' }}
              >
                {t('reporting.profile.paragraph')}
              </Typography>
            </Box>
          </Grid>

          {response && response.user_metadata && responseProfile && (
            <>
              <Divider />

              <HealthProfileSection
                title={t('common.onboardingSections.general')}
                cards={[
                  {
                    title: 'Name',
                    value: response.user_metadata.firstName,
                    valueColor: APP_SECTION_GENERAL,
                    span: isDesktop ? 4 : 6,
                    redirectPath: 'onboarding-general',
                    redirectStep: 1,
                  },
                  {
                    title: 'Age',
                    value: responseProfile.year_of_birth
                      ? '' +
                        (new Date().getFullYear() -
                          responseProfile.year_of_birth)
                      : '0',
                    valueColor: APP_SECTION_GENERAL,
                    span: isDesktop ? 4 : 6,
                    redirectPath: 'onboarding-general',
                    redirectStep: 1,
                  },
                  {
                    title: 'Biological sex',
                    value:
                      responseProfile.gender === 'male'
                        ? t('onboarding.generalSection.generalStep1.question2')
                        : t('onboarding.generalSection.generalStep1.question1'),
                    valueColor: APP_SECTION_GENERAL,
                    illustration:
                      responseProfile.gender === 'male' ? (
                        <MaleIcon height={'50px'} />
                      ) : (
                        <FemaleIcon height={'50px'} />
                      ),
                    span: isDesktop ? 4 : 12,
                    redirectPath: 'onboarding-general',
                    redirectStep: 1,
                  },
                  {
                    title: 'Ethnicitiy',
                    value: response.user_metadata.selectedEthnicity
                      ? getQuestionsEthnicity(null, t).find(
                          q =>
                            q.value === response.user_metadata.selectedEthnicity
                        ).label
                      : '-',
                    valueColor: APP_SECTION_GENERAL,
                    span: isDesktop ? 4 : 12,
                    redirectPath: 'onboarding-general',
                    redirectStep: 3,
                  },
                  {
                    title: 'Weight',
                    value:
                      (responseProfile &&
                      responseProfile.weight &&
                      response.user_metadata &&
                      response.user_metadata.selectedWeightMetric != null
                        ? getUserWeight(
                            responseProfile.weight,
                            response.user_metadata.selectedWeightMetric
                          )
                        : 0) +
                      ' ' +
                      (response.user_metadata &&
                      response.user_metadata.selectedWeightMetric != null
                        ? getWeightMetrics(t)[
                            response.user_metadata
                              ? response.user_metadata.selectedWeightMetric
                              : 0
                          ].metricName
                        : ''),
                    valueColor: APP_SECTION_GENERAL,
                    span: isDesktop ? 4 : 6,
                    redirectPath: 'onboarding-general',
                    redirectStep: 2,
                  },
                  {
                    title: 'Height',
                    value:
                      (responseProfile &&
                      responseProfile.height &&
                      response.user_metadata &&
                      response.user_metadata.selectedHeightMetric != null
                        ? getHeight(
                            responseProfile.height,
                            response.user_metadata.selectedHeightMetric
                          )
                        : 0) +
                      ' ' +
                      (response.user_metadata &&
                      response.user_metadata.selectedHeightMetric != null
                        ? getHeightMetrics(t)[
                            response.user_metadata
                              ? response.user_metadata.selectedHeightMetric
                              : 0
                          ].metricName
                        : ''),
                    valueColor: APP_SECTION_GENERAL,
                    span: isDesktop ? 4 : 6,
                    redirectPath: 'onboarding-general',
                    redirectStep: 2,
                  },
                  {
                    title: 'Country',
                    value: response.user_metadata.country
                      ? response.user_metadata.country
                      : '-',
                    valueColor: APP_SECTION_GENERAL,
                    span: isDesktop ? 6 : 12,
                    redirectPath: 'onboarding-general',
                    redirectStep: 3,
                  },
                  {
                    title: 'ZIP code',
                    value: response.user_metadata.zipCode
                      ? response.user_metadata.zipCode
                      : '-',
                    valueColor: APP_SECTION_GENERAL,
                    span: isDesktop ? 6 : 12,
                    redirectPath: 'onboarding-general',
                    redirectStep: 3,
                  },
                ]}
              />
            </>
          )}

          {responseCovidLifestyle && (
            <>
              <Divider />
              <HealthProfileSection
                title={t('common.onboardingSections.lifestyle')}
                cards={[
                  {
                    title: 'Sleep > 7 hours',
                    value:
                      responseCovidLifestyle['sleep_level'] != null
                        ? sleepAnswers[responseCovidLifestyle['sleep_level']]
                        : '-',
                    valueColor: APP_SECTION_LIFESTYLE,
                    span: isDesktop ? 6 : 12,
                    illustration:
                      responseCovidLifestyle['sleep_level'] != null ? (
                        <img
                          src={
                            sleepImages[responseCovidLifestyle['sleep_level']]
                              .image
                          }
                          style={{
                            height:
                              sleepImages[responseCovidLifestyle['sleep_level']]
                                .height,
                          }}
                          alt="sleep level illustration"
                        />
                      ) : null,
                    redirectPath: 'onboarding-lifestyle',
                    redirectStep: 1,
                  },
                  {
                    title: 'Stress level',
                    value:
                      responseCovidLifestyle['stress_level'] != null
                        ? stressAnswers[responseCovidLifestyle['stress_level']]
                        : '-',
                    valueColor: APP_SECTION_LIFESTYLE,
                    span: isDesktop ? 6 : 12,
                    illustration:
                      response.user_metadata.stress != null ? (
                        <img
                          src={
                            stressImages[responseCovidLifestyle['stress_level']]
                              .image
                          }
                          style={{
                            height:
                              stressImages[
                                responseCovidLifestyle['stress_level']
                              ].height,
                          }}
                          alt="stress level illustration"
                        />
                      ) : null,
                    redirectPath: 'onboarding-lifestyle',
                    redirectStep: 1,
                  },
                  {
                    title: 'Exercise, in the last 3 months',
                    value:
                      responseCovidLifestyle['exercise_level'] != null
                        ? exerciseAnswers[
                            responseCovidLifestyle['exercise_level']
                          ] +
                          (responseCovidLifestyle['exercise_level'] > 0
                            ? ' per week'
                            : '')
                        : '-',
                    valueColor: APP_SECTION_LIFESTYLE,
                    span: isDesktop ? 6 : 12,
                    illustration:
                      responseCovidLifestyle['exercise_level'] != null ? (
                        <img
                          src={
                            exerciseImages[
                              responseCovidLifestyle['exercise_level']
                            ].image
                          }
                          style={{
                            width:
                              exerciseImages[
                                responseCovidLifestyle['exercise_level']
                              ].width,
                          }}
                          alt="exercise level illustration"
                        />
                      ) : null,
                    redirectPath: 'onboarding-lifestyle',
                    redirectStep: 1,
                  },
                  {
                    title: 'Smoking',
                    value:
                      responseCovidLifestyle['smoking_level'] != null
                        ? responseCovidLifestyle['smoking_level'] !== 0
                          ? smokingAnswers[
                              responseCovidLifestyle['smoking_level']
                            ] + ' cigarettes per day'
                          : smokingAnswers[
                              responseCovidLifestyle['smoking_level']
                            ]
                        : '-',
                    valueColor: APP_SECTION_LIFESTYLE,
                    span: isDesktop ? 6 : 12,
                    illustration:
                      responseCovidLifestyle['smoking_level'] != null ? (
                        <img
                          src={
                            smokingImages[
                              responseCovidLifestyle['smoking_level']
                            ].image
                          }
                          style={{
                            width:
                              smokingImages[
                                responseCovidLifestyle['smoking_level']
                              ].width,
                          }}
                          alt="smoking level illustration"
                        />
                      ) : null,
                    redirectPath: 'onboarding-lifestyle',
                    redirectStep: 2,
                  },
                  {
                    title: 'Drinking, in the last 3 months',
                    value:
                      responseCovidLifestyle['drinking_level'] != null
                        ? drinkingAnswers[
                            responseCovidLifestyle['drinking_level']
                          ] +
                          (responseCovidLifestyle['drinking_level'] > 0
                            ? ' drinks per week'
                            : '')
                        : '-',
                    valueColor: APP_SECTION_LIFESTYLE,
                    span: 12,
                    illustration:
                      responseCovidLifestyle['drinking_level'] != null ? (
                        <img
                          src={
                            drinkingImages[
                              responseCovidLifestyle['drinking_level']
                            ].image
                          }
                          style={{
                            width:
                              drinkingImages[
                                responseCovidLifestyle['drinking_level']
                              ].width,
                          }}
                          alt="drinking level illustration"
                        />
                      ) : null,
                    redirectPath: 'onboarding-lifestyle',
                    redirectStep: 2,
                  },
                ]}
              />
            </>
          )}

          {/* {response &&
          response.user_metadata &&
          response.user_metadata.skipMedications === false &&
          responseACE &&
          responseARB && (
            <>
              <Divider />
              <HealthProfileSection
                title={t('common.onboardingSections.medications') + ' use'}
                cards={getMedicationCards(response.user_metadata)}
              />
            </>
          )} */}

          {response &&
            response.user_metadata &&
            responseCovidPastInfections &&
            responseCovidComorbidities &&
            responseCovidSymptoms &&
            responseDiseases &&
            responseACE &&
            responseARB && (
              <>
                <Divider />
                <HealthProfileSection
                  title={'Conditions'}
                  cards={[
                    {
                      title: 'Tested for COVID-19',
                      value:
                        getCovid19Tested() != null
                          ? getCovid19Tested().label
                          : '-',
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 1,
                    },
                    {
                      title: 'Tested for COVID-19 antibodies',
                      value:
                        getCovid19AntibodiesTested() != null
                          ? getCovid19AntibodiesTested().label
                          : '-',
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 2,
                    },
                    {
                      title: 'Contact with COVID-19 positive',
                      value: getContact() != null ? getContact().label : '-',
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 3,
                    },
                    {
                      ...getPreviouslyDiagnosedWithCard(),
                    },

                    ...getMedicationCards(response.user_metadata),
                    {
                      ...getHealthCard(),
                    },
                    {
                      title: 'Shortness of breath',
                      value: getSymptomsQuestions(t)[
                        responseCovidSymptoms.breath_shortness == null
                          ? 0
                          : responseCovidSymptoms.breath_shortness
                      ],
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 6,
                    },
                    {
                      title: 'Fever',
                      value: getSymptomsQuestions(t)[
                        responseCovidSymptoms.fever == null
                          ? 0
                          : responseCovidSymptoms.fever
                      ],
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 6,
                    },
                    {
                      title: 'Cough',
                      value: getSymptomsQuestions(t)[
                        responseCovidSymptoms.cough == null
                          ? 0
                          : responseCovidSymptoms.cough
                      ],
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 6,
                    },
                    {
                      title: 'Body pain and fatigue',
                      value: getSymptomsQuestions(t)[
                        responseCovidSymptoms.body_pain == null
                          ? 0
                          : responseCovidSymptoms.body_pain
                      ],
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 6,
                    },
                    {
                      title: 'Diarrhea',
                      value: getSymptomsQuestions(t)[
                        responseCovidSymptoms.diarrhea == null
                          ? 0
                          : responseCovidSymptoms.diarrhea
                      ],
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 6,
                    },
                    {
                      title: 'Runny nose',
                      value: getSymptomsQuestions(t)[
                        responseCovidSymptoms.runny_nose == null
                          ? 0
                          : responseCovidSymptoms.runny_nose
                      ],
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 6,
                    },
                    {
                      title: 'Sore throat',
                      value: getSymptomsQuestions(t)[
                        responseCovidSymptoms.sore_throat == null
                          ? 0
                          : responseCovidSymptoms.sore_throat
                      ],
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 6,
                    },
                    {
                      title: 'Headache',
                      value: getSymptomsQuestions(t)[
                        responseCovidSymptoms.headache == null
                          ? 0
                          : responseCovidSymptoms.headache
                      ],
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 6,
                    },
                    {
                      title: 'Memory loss or confusion',
                      value: getSymptomsQuestions(t)[
                        responseCovidSymptoms.memory_loss == null
                          ? 0
                          : responseCovidSymptoms.memory_loss
                      ],
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 6,
                    },
                    {
                      title: 'Loss of smell',
                      value: getSymptomsQuestions(t)[
                        responseCovidSymptoms.smell_loss == null
                          ? 0
                          : responseCovidSymptoms.smell_loss
                      ],
                      valueColor: APP_SECTION_DISEASE_HISTORY,
                      span: isDesktop ? 4 : 12,
                      redirectPath: 'onboarding-diseases',
                      redirectStep: 6,
                    },
                  ]}
                />
              </>
            )}

          {response && response.user_metadata && (
            <>
              <Divider />
              <HealthProfileSection
                title={t('common.onboardingSections.environment')}
                cards={getEnvironmentCards()}
              />
            </>
          )}
        </Grid>
      </Box>
    </AssessmentPage>
  ) : (
    <CustomDialog
      open={showError}
      onCloseButton={() => {
        window.location = '/';
      }}
      style={{ marginLeft: isDesktop ? '300px' : '0' }}
    >
      <ErrorMessageTile
        level="error"
        title={t('common.errorMessages.general.title')}
        message={t('common.errorMessages.general.message')}
        actionText={t('common.errorMessages.general.reloadAction')}
        onButtonClick={() => {
          window.location = '/';
        }}
      ></ErrorMessageTile>
    </CustomDialog>
  );
}
